import { ReadyRouteStatusEnum } from '@routellers/types/dist/city-ready-route/enum/ready-route-status.enum';
import { PartnerCityReadyRouteRO } from '@routellers/types/dist/partners-portal/partner-city-ready-route.ro';
import { Alert, Button, Card, Center, Group, Loader, Stack, Text, Title } from '@mantine/core';
import { IconMessageCircle, IconPlus, IconRoute } from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { api } from '../api';
import { PageHeader } from '../components/page-header';
import { RouteStatusBadge } from '../components/status-badge';
import { t } from '../dictionary';

export const RoutesPage = () => {
    const [routes, setRoutes] = useState<PartnerCityReadyRouteRO[] | null>(null);
    const navigate = useNavigate();

    useEffect(() => {
        api.routes()
            .then(({ data }) => setRoutes(data))
            .catch(() => setRoutes([]));
    }, []);

    if (!routes) {
        return (
            <Center h={300}>
                <Loader />
            </Center>
        );
    }

    return (
        <>
            <PageHeader
                title={t.routes.title}
                subtitle={t.routes.subtitle}
                action={
                    <Button component={Link} to='/routes/new' leftSection={<IconPlus size={16} />}>
                        {t.routes.newRoute}
                    </Button>
                }
            />

            {!routes.length && (
                <Card padding='xl'>
                    <Center py='xl'>
                        <Stack align='center' gap='sm' maw={420}>
                            <IconRoute
                                size={40}
                                stroke={1.2}
                                color='var(--mantine-color-indigo-5)'
                            />
                            <Title order={4}>{t.routes.empty}</Title>
                            <Text size='sm' c='dimmed' ta='center'>
                                {t.routes.emptyText}
                            </Text>
                            <Button component={Link} to='/routes/new' mt='xs'>
                                {t.routes.newRoute}
                            </Button>
                        </Stack>
                    </Center>
                </Card>
            )}

            <Stack gap='sm'>
                {routes.map((route) => (
                    <Card
                        key={route.id}
                        padding='lg'
                        style={{ cursor: 'pointer' }}
                        onClick={() => navigate(`/routes/${route.id}`)}
                    >
                        <Group justify='space-between' wrap='nowrap'>
                            <div style={{ minWidth: 0 }}>
                                <Text fw={650} truncate>
                                    {route.title || t.routes.untitled}
                                </Text>
                                <Text size='sm' c='dimmed' mt={2}>
                                    {[
                                        route.cityTitle,
                                        route.language?.toUpperCase(),
                                        t.routes.steps(route.stopsCount ?? route.stops?.length ?? 0),
                                    ]
                                        .filter(Boolean)
                                        .join(' · ')}
                                </Text>
                            </div>
                            <RouteStatusBadge status={route.status} />
                        </Group>

                        {route.status === ReadyRouteStatusEnum.REJECTED && route.rejectionReason && (
                            <Alert
                                mt='sm'
                                variant='light'
                                color='red'
                                icon={<IconMessageCircle size={16} />}
                                title={t.routes.rejectionTitle}
                            >
                                {route.rejectionReason}
                            </Alert>
                        )}
                    </Card>
                ))}
            </Stack>
        </>
    );
};
